import { useEffect, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { getAppDialogProps } from "../utils/dialogLayer";

export default function ConfirmDeleteDialog({
    open,
    onClose,
    onConfirm,
    title,
    description,
    confirmWord = "",
    confirmLabel = "Delete permanently",
    dangerous = true,
}) {
    const [typed, setTyped] = useState("");

    useEffect(() => {
        if (!open) {
            setTyped("");
        }
    }, [open]);

    const trimmedWord = String(confirmWord).trim();
    const canConfirm = trimmedWord.length > 0 && typed.trim() === trimmedWord;

    const handleConfirm = () => {
        if (!canConfirm) return;
        onConfirm();
    };

    const dialogLayer = getAppDialogProps({
        paperSx: {
            bgcolor: "#111418",
            color: "#f0ede8",
            backgroundImage: "none",
            border: "1px solid rgba(255,255,255,0.08)",
        },
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth {...dialogLayer}>
            <DialogTitle sx={{ color: "#f0ede8", fontFamily: '"DM Sans", sans-serif', pb: 1 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    {dangerous ? (
                        <WarningAmberOutlinedIcon sx={{ color: "error.main", mt: 0.25, flexShrink: 0 }} />
                    ) : null}
                    <Typography component="span" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.35 }}>
                        {title}
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <DialogContentText
                    sx={{
                        color: "rgba(240, 237, 232, 0.75)",
                        fontFamily: '"DM Sans", sans-serif',
                        mb: 2,
                    }}
                >
                    {description}
                </DialogContentText>
                {trimmedWord ? (
                    <Box>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "rgba(240, 237, 232, 0.85)",
                                fontFamily: '"DM Sans", sans-serif',
                                mb: 1,
                            }}
                        >
                            Type{" "}
                            <Box component="span" sx={{ color: "#e8c97e", fontWeight: 700 }}>
                                {trimmedWord}
                            </Box>{" "}
                            to confirm:
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            value={typed}
                            onChange={(e) => setTyped(e.target.value)}
                            autoComplete="off"
                            autoFocus
                            placeholder={trimmedWord}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    color: "#f0ede8",
                                    fontFamily: '"DM Sans", sans-serif',
                                    bgcolor: "rgba(255,255,255,0.04)",
                                    "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
                                    "&:hover fieldset": { borderColor: "rgba(232,201,126,0.4)" },
                                    "&.Mui-focused fieldset": { borderColor: "#e8c97e" },
                                },
                            }}
                        />
                    </Box>
                ) : null}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
                <Button
                    onClick={onClose}
                    sx={{
                        color: "rgba(240, 237, 232, 0.8)",
                        fontFamily: '"DM Sans", sans-serif',
                        textTransform: "none",
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color={dangerous ? "error" : "primary"}
                    disabled={trimmedWord ? !canConfirm : false}
                    onClick={handleConfirm}
                    sx={{
                        fontFamily: '"DM Sans", sans-serif',
                        textTransform: "none",
                        fontWeight: 600,
                    }}
                >
                    {confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
